import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import { greetingTitleStyles } from 'lib/typography/greeting-title-styles'
import { useAuth } from 'lib/auth'
import { getGreeting } from 'utils/greeting'
import { PGA_COM_HOST } from 'env'
import SocialShareDialog from 'components/social-share-dialog'
import ActionsMenu from 'components/actions-menu'
import LinkIcon from 'components/material-symbol/icons/Link'
import EventAvailableOutlinedIcon from 'components/material-symbol/icons/EventAvailable'
import AddIcon from 'components/material-symbol/icons/Add'
import SendIcon from 'components/material-symbol/icons/Send'
import MoreHorizIcon from 'components/material-symbol/icons/MoreHoriz'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4, 4.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2),
    },
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  titleBlock: {
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: 520,
  },
  greeting: {
    display: 'block',
    marginBottom: theme.spacing(1),
    ...greetingTitleStyles(theme),
  },
  subtitle: {
    display: 'block',
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    lineHeight: 1.5,
    maxWidth: 480,
  },
  actions: {
    display: 'flex',
    flex: '0 0 auto',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
    },
  },
  overflowButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50px',
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#E0E0E0',
      boxShadow: 'none',
    },
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
}))

const DashboardHeader = ({ slug, variant = 'default' }) => {
  const classes = useStyles()
  const { user } = useAuth()
  const firstName = user?.firstName || user?.name?.split(' ')?.[0] || 'Coach'
  const shareUrl =
    user?.coach?.bookingUrl || (slug ? `${PGA_COM_HOST}/coach/${slug}` : null)
  const isOnboarding = variant === 'onboarding'
  const subtitle = isOnboarding
    ? 'Finish these steps to start taking bookings on PGA Coach.'
    : "Here's what's happening in your coaching business today."

  return (
    <Box className={classes.root}>
      <Box className={classes.headerRow}>
        <Box className={classes.titleBlock}>
          <Typography component="h1" className={classes.greeting}>
            {getGreeting()}, {firstName}
          </Typography>
          <Typography component="p" className={classes.subtitle}>
            {subtitle}
          </Typography>
        </Box>
        {!isOnboarding ? (
          <Box className={classes.actions}>
            {shareUrl ? (
              <SocialShareDialog
                trigger={
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<LinkIcon />}
                  >
                    Share Booking Link
                  </Button>
                }
                shareUrl={shareUrl}
                shareTitle="Booking Link"
                showBookingUrl
                att="booking-link"
              />
            ) : null}
            <ActionsMenu
              ariaId="dashboard-header"
              buttonContent={<MoreHorizIcon />}
              className={classes.overflowButton}
              color="default"
              variant="outlined"
            >
              <MenuItem
                component={RouterLink}
                to="/pga-coach/settings/availability"
              >
                <ListItemIcon className={classes.menuIcon}>
                  <EventAvailableOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Add Availability" />
              </MenuItem>
              <MenuItem
                component={RouterLink}
                to="/pga-coach/bookings/offerings"
              >
                <ListItemIcon className={classes.menuIcon}>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Create Offering" />
              </MenuItem>
              <MenuItem component={RouterLink} to="/pga-coach/messages">
                <ListItemIcon className={classes.menuIcon}>
                  <SendIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Message Students" />
              </MenuItem>
            </ActionsMenu>
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}

export default DashboardHeader
